/**
 * Read-only display for the `permissions` property field type.
 *
 * The stored value is a JSON list of permission grant objects
 * (`{ entity_type_name, event_type_name?, scoping_association_type_name?,
 * scoped_target_entity_id? }`). One data type routinely spans SEVERAL grants —
 * the permission engine unions scopings, so "Meeting Notes, public and private"
 * is stored as one org-targeted grant plus one user-targeted grant. Rendering a
 * badge per raw grant therefore prints the same type twice, which is why grants
 * are grouped by type here and the scope is folded into a single badge.
 *
 * Scope is classified by each scoping association's declared TARGET
 * (`ahoy_org` = public, `ahoy_user` = private), never by its name — see
 * `api-access-service.ts` for the same rule on the write side.
 *
 * Used by `PropertyValueLabel.tsx` when `field_type_name === 'permissions'`.
 * There is deliberately no matching input component — permission grants are
 * write-once at creation (`value_update_access: none`), so the record page
 * only ever renders them read-only.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import {
  entityTypesQueryKey,
  fetchEntityTypeSchemas,
} from '@/lib/crm/hooks/useEntityTypeSchema';
import {
  entryHasAnyScoping,
  getPropertyScoping,
  parsePermissionGrantEntries,
} from '@/lib/permissions/grant-scoping';
import {
  SCOPE_LABEL_ALL_EXCEPT_PRIVATE,
  SCOPE_LABEL_ALL_INCLUDING_PRIVATE,
} from '@/lib/permissions/scope-labels';

export interface PermissionGrant {
  entity_type_name: string;
  event_type_name?: string;
  scoping_association_type_name?: string;
  scoped_target_entity_id?: string;
  scoping_property_type_name?: string;
  scoping_property_value?: string;
}

interface PermissionsValueLabelProps {
  /** Stored value — the grants list, or its raw JSON string form */
  value: unknown;
  /** Organization ID used to resolve entity type schema labels */
  orgId?: string | number;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** When true, collapse to a single line with "+N more" for tables/inline use */
  truncate?: boolean;
  /**
   * `badges` (default) renders one badge per (type, event) pair — compact,
   * for list cells and generic property rows. `rows` renders one line per
   * data type with its read scope and write level side by side — the shape a
   * credential record uses, where an admin scans "what can this key touch"
   * type by type rather than reading a wall of chips.
   */
  layout?: 'badges' | 'rows';
}

const fontSizeClasses: Record<string, string> = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

const SCHEMA_STALE_TIME_MS = 5 * 60 * 1000;

/** "ahoy_property_updated" → "Property updated" */
const humanizeEventName = (eventTypeName: string): string => {
  const formatted = eventTypeName.replace(/^ahoy_/, '').replace(/_/g, ' ');
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

/** "ahoy_unowned" → "Unowned" */
const humanizeName = (name: string): string => {
  const formatted = name.replace(/^ahoy_/, '').replace(/_/g, ' ');
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

/** Target entity type of a scoping association that means "public" (org-wide). */
const PUBLIC_TARGET_ENTITY_TYPE = 'ahoy_org';
/** Target entity type of a scoping association that means "private to a user". */
const PRIVATE_TARGET_ENTITY_TYPE = 'ahoy_user';

interface GroupedGrant {
  key: string;
  entityTypeName: string;
  eventTypeName: string;
  /** At least one grant carries no scoping: the whole type is covered. */
  unrestricted: boolean;
  hasPublic: boolean;
  hasPrivate: boolean;
  propertyScopings: Array<{ typeName: string; value: string }>;
  /** At least one grant carries a scoping key we don't recognise — still scoped. */
  hasUnknownScoping: boolean;
}

/**
 * Fold the raw grants into one entry per (entity type, event type) pair,
 * recording which scopes are present. Several grants for the same type is the
 * normal case, not a duplicate.
 */
const groupGrants = (
  grants: PermissionGrant[],
  associationTargets: ReadonlyMap<string, string>
): GroupedGrant[] => {
  const byKey = new Map<string, GroupedGrant>();
  for (const grant of grants) {
    const eventTypeName = grant.event_type_name ?? '';
    const key = `${grant.entity_type_name}:${eventTypeName}`;
    let entry = byKey.get(key);
    if (!entry) {
      entry = {
        key,
        entityTypeName: grant.entity_type_name,
        eventTypeName,
        unrestricted: false,
        hasPublic: false,
        hasPrivate: false,
        propertyScopings: [],
        hasUnknownScoping: false,
      };
      byKey.set(key, entry);
    }
    if (!entryHasAnyScoping(grant as unknown as Record<string, unknown>)) {
      entry.unrestricted = true;
      continue;
    }
    const property = getPropertyScoping(
      grant as unknown as Record<string, unknown>
    );
    if (property) {
      entry.propertyScopings.push(property);
    }
    const assoc = grant.scoping_association_type_name;
    if (assoc) {
      const target = associationTargets.get(assoc);
      if (target === PUBLIC_TARGET_ENTITY_TYPE) {
        entry.hasPublic = true;
      } else if (target === PRIVATE_TARGET_ENTITY_TYPE) {
        entry.hasPrivate = true;
      }
    }
    if (!assoc && !property) {
      entry.hasUnknownScoping = true;
    }
  }
  return [...byKey.values()];
};

/**
 * Human-readable scope for one grouped grant, or null when a scoping cannot be
 * classified — an unrecognised target must never be guessed at, so it renders
 * with no scope word rather than an inferred one.
 *
 * An unrestricted grant is stated as "All records", NOT left blank: it is the
 * broadest grant there is, and rendering it as the absence of a scope made it
 * look like the least specified one next to "Public" or "Private".
 */
const describeScope = (entry: GroupedGrant): string | null => {
  if (entry.unrestricted) {
    return 'All records';
  }
  const parts: string[] = [];
  if (entry.hasPublic && entry.hasPrivate) {
    parts.push(SCOPE_LABEL_ALL_INCLUDING_PRIVATE);
  } else if (entry.hasPublic) {
    parts.push(SCOPE_LABEL_ALL_EXCEPT_PRIVATE);
  } else if (entry.hasPrivate) {
    parts.push('Private');
  }
  for (const p of entry.propertyScopings) {
    parts.push(
      p.value === ''
        ? humanizeName(p.typeName)
        : `${humanizeName(p.typeName)}: ${p.value}`
    );
  }
  if (entry.hasUnknownScoping) {
    parts.push('Scoped');
  }
  return parts.length > 0 ? parts.join(', ') : null;
};

/** The two association events; a type whose only writes are these can link, not edit. */
const ASSOCIATION_EVENT_TYPES = new Set([
  'ahoy_association_added',
  'ahoy_association_removed',
]);

interface TypeRow {
  entityTypeName: string;
  label: string;
  /** Read scope for the type, or null when the type has no read grant. */
  readScope: string | null;
  /** `write` when any non-association event is granted, `link` when only association events are. */
  writeLevel: 'write' | 'link' | null;
}

/** Fold grouped (type, event) entries into one row per data type. */
const buildTypeRows = (
  entries: GroupedGrant[],
  labelByType: ReadonlyMap<string, string>
): TypeRow[] => {
  const byType = new Map<string, TypeRow>();
  for (const entry of entries) {
    let row = byType.get(entry.entityTypeName);
    if (!row) {
      row = {
        entityTypeName: entry.entityTypeName,
        label: labelByType.get(entry.entityTypeName) ?? entry.entityTypeName,
        readScope: null,
        writeLevel: null,
      };
      byType.set(entry.entityTypeName, row);
    }
    if (!entry.eventTypeName) {
      row.readScope = describeScope(entry);
    } else if (!ASSOCIATION_EVENT_TYPES.has(entry.eventTypeName)) {
      row.writeLevel = 'write';
    } else if (row.writeLevel === null) {
      row.writeLevel = 'link';
    }
  }
  return [...byType.values()].sort((a, b) => a.label.localeCompare(b.label));
};

export const PermissionsValueLabel: React.FC<PermissionsValueLabelProps> = ({
  value,
  orgId,
  fontSize = 'sm',
  className = '',
  truncate,
  layout = 'badges',
}) => {
  const sizeClass = fontSizeClasses[fontSize];

  const grants = useMemo(
    () => parsePermissionGrantEntries(value) as unknown as PermissionGrant[],
    [value]
  );

  const enabled = !!orgId && grants.length > 0;
  const { data: schemas } = useQuery({
    queryKey: entityTypesQueryKey(orgId),
    queryFn: () => fetchEntityTypeSchemas(orgId as string | number),
    enabled,
    staleTime: SCHEMA_STALE_TIME_MS,
  });
  // Scope classification needs each scoping association's declared target.
  const { associationTypeMap } = useAssociationTypes(
    orgId === undefined ? undefined : Number(orgId)
  );

  const labelByType = useMemo(() => {
    const labels = new Map<string, string>();
    for (const schema of schemas ?? []) {
      // Plural: a grant covers every record of the type, so "Contacts" states
      // the scope. "Contact" reads as a single record and understates it.
      labels.set(schema.entity_type_name, schema.label_plural || schema.label);
    }
    return labels;
  }, [schemas]);

  const groupedEntries = useMemo(
    () => groupGrants(grants, associationTypeMap),
    [grants, associationTypeMap]
  );

  const typeRows = useMemo(
    () => buildTypeRows(groupedEntries, labelByType),
    [groupedEntries, labelByType]
  );

  const grantEntries = useMemo(() => {
    return groupedEntries.map(entry => {
      const typeLabel =
        labelByType.get(entry.entityTypeName) ?? entry.entityTypeName;
      const parts = [typeLabel];
      // The permission engine encodes "read" as the ABSENCE of an event type
      // (see PERMISSIONS.md — an entry with no event_type_name IS the read
      // permission). Rendering nothing for that case left the access level
      // implicit, so a read grant read as unqualified access next to a write
      // grant that names its event. State it.
      parts.push(
        entry.eventTypeName ? humanizeEventName(entry.eventTypeName) : 'Read'
      );
      const scope = describeScope(entry);
      if (scope) {
        parts.push(scope);
      }
      return { key: entry.key, label: parts.join(' · ') };
    });
  }, [groupedEntries, labelByType]);

  if (grantEntries.length === 0) {
    return (
      <span className={`${sizeClass} ${className}`} title='No permissions'>
        —
      </span>
    );
  }

  if (layout === 'rows') {
    return (
      <ul
        className={`${sizeClass} flex flex-col divide-y divide-border-secondary ${className}`}
        aria-label='Data access by type'
      >
        {typeRows.map(row => (
          <li
            key={row.entityTypeName}
            className='flex items-center justify-between gap-3 py-1.5'
          >
            <span className='min-w-0 truncate text-fg-primary'>
              {row.label}
            </span>
            <span className='flex shrink-0 items-center gap-1'>
              {row.readScope && (
                <Badge color='gray' size='sm'>
                  {row.readScope}
                </Badge>
              )}
              {row.writeLevel === 'write' && (
                <Badge color='success' size='sm'>
                  Can write
                </Badge>
              )}
              {row.writeLevel === 'link' && (
                <Badge color='blue' size='sm'>
                  Can link
                </Badge>
              )}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  const fullTitle = grantEntries.map(entry => entry.label).join(', ');

  if (truncate) {
    const remaining = grantEntries.length - 1;
    return (
      <span
        className={`${sizeClass} block truncate ${className}`}
        title={fullTitle}
      >
        {grantEntries[0].label}
        {remaining > 0 ? ` · +${remaining} more` : ''}
      </span>
    );
  }

  return (
    <span
      className={`${sizeClass} inline-flex flex-wrap items-center gap-1 ${className}`}
      title={fullTitle}
    >
      {grantEntries.map(entry => (
        <Badge key={entry.key} color='gray' size='sm'>
          {entry.label}
        </Badge>
      ))}
    </span>
  );
};
